import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
// Material-UI
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import Button from '@material-ui/core/Button';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { DataGrid, esES } from '@material-ui/data-grid';
// Icons
import { CirclePlus, Edit, Trash, X } from 'tabler-icons-react';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
// Context
import { useGeneral } from '../../Context/GeneralContext';
// Styles
import '../../styles/diasFestivos.css';

function formatFecha(fechaStr) {
  const [year, month, day] = fechaStr.split('-');
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'long' });
}

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

const tableColumns = [
  { field: 'nombre', headerName: 'Nombre', flex: 1 },
  { field: 'fecha', headerName: 'Fecha', flex: 0.6 },
];

function QuickSearchToolbar({ value, onChange, clearSearch }) {
  return (
    <div className="table-search-toolbar">
      <TextField
        variant="outlined"
        size="small"
        value={value}
        onChange={onChange}
        placeholder="Buscar..."
        fullWidth
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" />,
          endAdornment: (
            <IconButton
              aria-label="Limpiar búsqueda"
              size="small"
              className={value ? 'table-search-clear-visible' : 'table-search-clear-hidden'}
              onClick={clearSearch}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />
    </div>
  );
}

QuickSearchToolbar.propTypes = {
  clearSearch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

export default function Holidays() {
  const { tokenR } = useGeneral();
  const [selectionModel, setSelectionModel] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [holidays, setHolidays] = useState([]);
  const [rows, setRows] = useState([]);

  // ── Modal Agregar ─────────────────────────────────────────
  const [addOpen, setAddOpen] = useState(false);
  const [addNombre, setAddNombre] = useState('');
  const [addNombreError, setAddNombreError] = useState(false);
  const [addDate, setAddDate] = useState(new Date());

  const handleAddOpen = () => {
    setAddNombre('');
    setAddNombreError(false);
    setAddDate(new Date());
    setAddOpen(true);
  };
  const handleAddClose = () => setAddOpen(false);

  // ── Modal Editar ──────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editNombre, setEditNombre] = useState('');
  const [editNombreError, setEditNombreError] = useState(false);
  const [editDate, setEditDate] = useState(new Date());

  const handleEditOpen = () => {
    const selected = holidays.find(h => h.id === selectionModel[0]);
    if (!selected) return;
    setEditNombre(selected.nombre);
    setEditNombreError(false);
    const [year, month, day] = selected.fechaRaw.split('-');
    setEditDate(new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)));
    setEditOpen(true);
  };
  const handleEditClose = () => setEditOpen(false);

  // ── Modal Eliminar ────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const handleDeleteOpen = () => setDeleteOpen(true);
  const handleDeleteClose = () => setDeleteOpen(false);

  // ── Helpers ───────────────────────────────────────────────
  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    setRows(holidays.filter(row =>
      Object.keys(row).some(field => searchRegex.test(row[field].toString()))
    ));
  };

  const buildFecha = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}T17:45:14.517`;
  };

  // ── API ───────────────────────────────────────────────────
  const getHolidays = useCallback(async () => {
    const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/holidays`, {
      method: 'GET',
      headers: { authorization: `Bearer ${tokenR.current}` },
    });
    const data = await res.json();
    const array = data.map((element, i) => ({
      id: element.id ?? i,
      nombre: element.nombre,
      fecha: formatFecha(element.fecha),
      fechaRaw: element.fecha,
    }));
    setHolidays(array);
  }, [tokenR]);

  const postHoliday = useCallback(async (body) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/holidays`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          authorization: `Bearer ${tokenR.current}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) { console.error('Error al guardar día festivo:', res.status); return; }
      await getHolidays();
    } catch (err) { console.error('Error al guardar día festivo:', err); }
  }, [tokenR, getHolidays]);

  const putHoliday = useCallback(async (id, body) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/holidays/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: {
          authorization: `Bearer ${tokenR.current}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) { console.error('Error al editar día festivo:', res.status); return; }
      await getHolidays();
    } catch (err) { console.error('Error al editar día festivo:', err); }
  }, [tokenR, getHolidays]);

  const deleteHoliday = useCallback(async (id) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/holidays/${id}`, {
        method: 'DELETE',
        headers: { authorization: `Bearer ${tokenR.current}` },
      });
      if (!res.ok) { console.error('Error al eliminar día festivo:', res.status); return; }
      setSelectionModel([]);
      await getHolidays();
    } catch (err) { console.error('Error al eliminar día festivo:', err); }
  }, [tokenR, getHolidays]);

  useEffect(() => { getHolidays(); }, [getHolidays]);
  useEffect(() => { setRows(holidays); }, [holidays]);

  // ── Handlers ──────────────────────────────────────────────
  const createHoliday = () => {
    if (!addNombre.trim()) { setAddNombreError(true); return; }
    postHoliday({ nombre: addNombre.trim(), descripcion: 'Sin descripcion', fecha: buildFecha(addDate) });
    handleAddClose();
  };

  const updateHoliday = () => {
    if (!editNombre.trim()) { setEditNombreError(true); return; }
    const selected = holidays.find(h => h.id === selectionModel[0]);
    if (!selected) return;
    putHoliday(selected.id, { nombre: editNombre.trim(), descripcion: 'Sin descripcion', fecha: buildFecha(editDate) });
    handleEditClose();
  };

  const confirmDelete = () => {
    selectionModel.forEach(id => deleteHoliday(id));
    handleDeleteClose();
  };

  const hasSelection = selectionModel.length > 0;
  const hasSingle = selectionModel.length === 1;

  return (
    <div className="df-view">
      <div className="df-header">
        <h1 className="df-title">Días festivos</h1>
        <p className="df-desc">Selecciona cuáles días festivos aplican en la compañía</p>
      </div>

      <div className="df-table-card">
        <div className="df-card-toolbar">
          <span className="df-selected-count">{selectionModel.length} seleccionados</span>
          <div className="df-card-actions">
            <Button
              variant="text"
              className="df-toolbar-btn"
              onClick={handleAddOpen}
            >
              <CirclePlus size={16} strokeWidth={2} />
              Agregar
            </Button>
            <Button
              variant="text"
              className="df-toolbar-btn"
              disabled={!hasSingle}
              onClick={handleEditOpen}
            >
              <Edit size={16} strokeWidth={2} />
              Editar
            </Button>
            <Button
              variant="text"
              className="df-toolbar-btn df-toolbar-btn--danger"
              disabled={!hasSelection}
              onClick={handleDeleteOpen}
            >
              <Trash size={16} strokeWidth={2} />
              Eliminar
            </Button>
          </div>
        </div>

        <div className="df-table-wrapper">
          <DataGrid
            components={{ Toolbar: QuickSearchToolbar }}
            componentsProps={{
              toolbar: {
                value: searchText,
                onChange: e => requestSearch(e.target.value),
                clearSearch: () => requestSearch(''),
              },
            }}
            columns={tableColumns}
            rows={rows}
            localeText={esES.props.MuiDataGrid.localeText}
            checkboxSelection
            disableColumnFilter
            disableDensitySelector
            hideFooterSelectedRowCount
            onSelectionModelChange={setSelectionModel}
            selectionModel={selectionModel}
          />
        </div>
      </div>

      {/* ── Modal Agregar ─────────────────────────────────── */}
      <Modal open={addOpen} onClose={handleAddClose}>
        <div className="df-modal">
          <div className="df-modal-header">
            <h2 className="df-modal-title">Nuevo día festivo</h2>
            <IconButton size="small" onClick={handleAddClose}>
              <X size={18} strokeWidth={2} />
            </IconButton>
          </div>
          <div className="df-modal-body">
            <FormControl fullWidth error={addNombreError}>
              <InputLabel htmlFor="df-add-nombre">Nombre</InputLabel>
              <Input
                id="df-add-nombre"
                value={addNombre}
                onChange={e => { setAddNombre(e.target.value); if (e.target.value.trim()) setAddNombreError(false); }}
              />
              <FormHelperText>{addNombreError ? 'Debe diligenciar el nombre' : ''}</FormHelperText>
            </FormControl>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                label="Fecha"
                value={addDate}
                onChange={setAddDate}
                fullWidth
                KeyboardButtonProps={{ 'aria-label': 'cambiar fecha', type: 'button' }}
              />
            </MuiPickersUtilsProvider>
          </div>
          <div className="df-modal-footer">
            <Button variant="outlined" className="df-modal-cancel" onClick={handleAddClose}>Cancelar</Button>
            <Button variant="contained" color="primary" className="df-modal-submit" onClick={createHoliday}>Agregar</Button>
          </div>
        </div>
      </Modal>

      {/* ── Modal Editar ──────────────────────────────────── */}
      <Modal open={editOpen} onClose={handleEditClose}>
        <div className="df-modal">
          <div className="df-modal-header">
            <h2 className="df-modal-title">Editar día festivo</h2>
            <IconButton size="small" onClick={handleEditClose}>
              <X size={18} strokeWidth={2} />
            </IconButton>
          </div>
          <div className="df-modal-body">
            <FormControl fullWidth error={editNombreError}>
              <InputLabel htmlFor="df-edit-nombre">Nombre</InputLabel>
              <Input
                id="df-edit-nombre"
                value={editNombre}
                onChange={e => { setEditNombre(e.target.value); if (e.target.value.trim()) setEditNombreError(false); }}
              />
              <FormHelperText>{editNombreError ? 'Debe diligenciar el nombre' : ''}</FormHelperText>
            </FormControl>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                label="Fecha"
                value={editDate}
                onChange={setEditDate}
                fullWidth
                KeyboardButtonProps={{ 'aria-label': 'cambiar fecha', type: 'button' }}
              />
            </MuiPickersUtilsProvider>
          </div>
          <div className="df-modal-footer">
            <Button variant="outlined" className="df-modal-cancel" onClick={handleEditClose}>Cancelar</Button>
            <Button variant="contained" color="primary" className="df-modal-submit" onClick={updateHoliday}>Guardar</Button>
          </div>
        </div>
      </Modal>

      {/* ── Modal Confirmar Eliminar ──────────────────────── */}
      <Modal open={deleteOpen} onClose={handleDeleteClose}>
        <div className="df-modal">
          <div className="df-modal-header">
            <h2 className="df-modal-title">Eliminar día festivo</h2>
            <IconButton size="small" onClick={handleDeleteClose}>
              <X size={18} strokeWidth={2} />
            </IconButton>
          </div>
          <div className="df-modal-body df-modal-body--confirm">
            <Trash size={56} color="#7D7F90" strokeWidth={1.5} />
            <p className="df-confirm-text">
              ¿Está seguro que desea eliminar {selectionModel.length === 1 ? 'el día festivo seleccionado' : `los ${selectionModel.length} días festivos seleccionados`}?
            </p>
          </div>
          <div className="df-modal-footer">
            <Button variant="outlined" className="df-modal-cancel" onClick={handleDeleteClose}>Cancelar</Button>
            <Button variant="contained" color="secondary" className="df-modal-delete" onClick={confirmDelete}>Eliminar</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
